import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import {TextInput, View, FlatList, TouchableOpacity, Dimensions, ActivityIndicator, ScrollView} from 'react-native';
import EStyleSheet from "react-native-extended-stylesheet";
import {useDebouncedCallback} from 'use-debounce';
import {CancelToken} from "axios";
import {connect} from 'react-redux';

import AppText from "../../components/AppText";
import MessagesService from "../../services/MessagesService";
import AppButton from "../../components/AppButton";
import {basicStyles} from "../../styles/basic";
import {getMinuetsSinceDate, formatDate, formatPhoneNumber, getUserDisplayName} from "../../utilities/methods";
import MessageFeedResolvedSvg from "../../components/svgs/MessageFeedResolvedSvg";
import MessageFeedUnresolvedSvg from "../../components/svgs/MessageFeedUnresolvedSvg";
import {navigate} from "../../utilities/navigation";
import CreateNewMessageModal from "./components/CreateNewMessageModal";
import MessageFeed from "./components/MessageFeed";
import AppPickerInput from "../../components/AppPickerInput";
import UserOrganizationsService from "../../services/UserOrganizationsService";
import {setOrganization, setOrganizationRoleId, setOrganizationRoleToken} from "../../store/auth/authActions";
import {bindActionCreators} from 'redux';
import AsyncStorage from '@react-native-community/async-storage';
import UsersService from "../../services/UsersService";
import {DarkModeProvider, useDarkMode} from "./DarkModeContext";
import {getThemeTransitionStyle} from "./theme";
import ChevronDownSvg from "../../components/svgs/ChevronDownSvg";
import AdjustmentsHorizontalSvg from "../../components/svgs/AdjustmentsHorizontalSvg";
import ChecklistSvg from "../../components/svgs/ChecklistSvg";
import MailAltSvg from "../../components/svgs/MailAltSvg";
import MessageSvg from "../../components/svgs/MessageSvg";
import CloseSvg from '../../components/svgs/CloseSvg';
import UnresolvedStatusBanner from './components/UnresolvedStatusBanner';
import useInboxAiGrammar from '../../hooks/useInboxAiGrammar';

function MessageFlowInner(props) {
    const {isDarkMode, theme} = useDarkMode();
    const {realToken, user, organization} = props.auth;
    const organizationId = props.auth.organization.organization_id;
    // the organizationId param records which account a feed selection was made under;
    // a selection from a different account (stale after an account switch) must not be honored
    const selectedFeedOrganizationId = props.route?.params?.organizationId;
    const selectedMessageFeedId = (!selectedFeedOrganizationId || selectedFeedOrganizationId == organizationId)
        ? props.route?.params?.messageFeedId
        : undefined;
    const [messageFeeds, setMessageFeeds] = useState([]);
    const [totalFilteredCount, setTotalFilteredCount] = useState('--');
    const [newFeeds, setNewFeeds] = useState([]);
    const [filters, setFilters] = useState({
        search: null,
        message_feed_resolved: '0',
        selectedUserIds: [],
        includeUnassigned: false,
        sortOrder: 'newest',
        activeTypes: ['text', 'email', 'task'],
    });
    const [filtersInitialized, setFiltersInitialized] = useState(false);
    const [page, setPage] = useState(0);
    const [isLastPage, setIsLastPage] = useState(false);
    const [newMessageModalActive, setNewMessageModalActive] = useState({active: false, type: null});
    const perPage = 20;
    const fetchDataRequestSource = useRef(null);
    const fetchTimeout = useRef(null);
    const lastRefreshTimestamp = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const inboxAiGrammar = useInboxAiGrammar({
        organizationId,
        userId: user?.user_id,
        sessionKey: realToken || props.auth.token,
    });

    const {screenHeight, screenWidth} = useMemo(() => {
        return {
            screenHeight: Dimensions.get('window').height,
            screenWidth: Dimensions.get('window').width,
        };
    });


    const [blockFilterTaps, setBlockFilterTaps] = useState(false);

    // Arm the guard as early as possible (touchstart), before the browser's click-through
    // click can retarget onto the account picker and open it once the feed detail unmounts.
    const armCloseFeedGuard = () => {
        if (screenWidth <= 550) {
            setBlockFilterTaps(true);
            // Safety disarm in case the press is cancelled (drag-off) and onPress never fires.
            setTimeout(() => setBlockFilterTaps(false), 600);
        }
    };

    const closeFeed = () => {
        if (screenWidth <= 550) {
            setBlockFilterTaps(true);
            setTimeout(() => setBlockFilterTaps(false), 400);
        }
        navigate('MessageFlow', {messageFeedId: null});
    };

    const [messageFeedUsers, setMessageFeedUsers] = useState([]);
    const [userCountsUnresolved, setUserCountsUnresolved] = useState(null);
    const [userOrganizations, setUserOrganizations] = useState([]);
    const [messageTypes, setMessageTypes] = useState([]);

    // UI state for picker dropdowns/sheets
    const [userPickerOpen, setUserPickerOpen] = useState(false);
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [userSheetOpen, setUserSheetOpen] = useState(false);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    const [userDropdownPos, setUserDropdownPos] = useState({top: 0, left: 0, width: 280});
    const [filterDropdownPos, setFilterDropdownPos] = useState({top: 0, left: 0});

    const containerRef = useRef(null);
    const userPickerRef = useRef(null);
    const filterMenuRef = useRef(null);
    const userDropdownRef = useRef(null);
    const filterDropdownRef = useRef(null);

    // which account the current filters state belongs to; saves are blocked until
    // loadFilters has run for the active account
    const filtersOrganizationId = useRef(null);

    const loadFilters = useCallback(async (organization) => {
        const userFilterIds = await AsyncStorage.getItem('feed-filter:users:' + user.user_id + ':' + organization.organization_id)
        if (userFilterIds) {
            console.log('loading filters', 'feed-filter:users:' + user.user_id + ':' + organization.organization_id, userFilterIds);
            setFilters(prev => ({
                ...prev,
                selectedUserIds: userFilterIds.split(','),
            }))
        } else {
            setFilters(prev => ({...prev, selectedUserIds: []}))
        }
        const saveUnassigned = await AsyncStorage.getItem('feed-filter:unassigned:' + user.user_id + ':' + organization.organization_id)
        if (saveUnassigned) {
            setFilters(prev => ({...prev, includeUnassigned: saveUnassigned === 'true'}))
        } else {
            setFilters(prev => ({...prev, includeUnassigned: false}))
        }
        const selectedTypesString = await AsyncStorage.getItem('feed-filter:types:' + user.user_id + ':' + organization.organization_id)
        if (selectedTypesString) {
            setFilters(prev => ({...prev, activeTypes: selectedTypesString.split(',')}))
        } else {
            setFilters(prev => ({...prev, activeTypes: ['text', 'email', 'task']}))
        }
        const selectedSortString = await AsyncStorage.getItem('feed-filter:sort:' + user.user_id + ':' + organization.organization_id)
        if (selectedSortString) {
            setFilters(prev => ({...prev, sortOrder: selectedSortString}))
        } else {
            setFilters(prev => ({...prev, sortOrder: 'newest'}))
        }
        const selectedStatus = await AsyncStorage.getItem('feed-filter:status:' + user.user_id + ':' + organization.organization_id)
        if (selectedStatus) {
            setFilters(prev => ({...prev, message_feed_resolved: selectedStatus}))
        } else {
            setFilters(prev => ({...prev, message_feed_resolved: '0'}))
        }
        filtersOrganizationId.current = organization.organization_id;
        setFiltersInitialized(true);
    }, []);

    useEffect(() => {
        if (filtersOrganizationId.current != organization.organization_id) {
            setFiltersInitialized(false);
            void loadFilters(organization);
        }
    }, [organization]);

    useEffect(() => {
        // filtersOrganizationId lags the account change until loadFilters completes, so this
        // also keeps the old account's filters from being saved under the new account's keys
        if (!filtersInitialized || filtersOrganizationId.current != organization.organization_id) return;

        (async () => {
            console.log('saving filters', 'feed-filter:users:' + user.user_id + ':' + organization.organization_id, filters.selectedUserIds.join(','));
            await AsyncStorage.setItem('feed-filter:users:' + user.user_id + ':' + organization.organization_id, filters.selectedUserIds.join(','));
            await AsyncStorage.setItem('feed-filter:types:' + user.user_id + ':' + organization.organization_id, filters.activeTypes.join(','));
            await AsyncStorage.setItem('feed-filter:sort:' + user.user_id + ':' + organization.organization_id, filters.sortOrder);
            await AsyncStorage.setItem('feed-filter:status:' + user.user_id + ':' + organization.organization_id, filters.message_feed_resolved);
            await AsyncStorage.setItem('feed-filter:unassigned:' + user.user_id + ':' + organization.organization_id, String(filters.includeUnassigned));
        })()
    }, [filters, filtersInitialized, organization]);

    const switchOrganization = async (organization) => {
        if (!organization || organization.organization_id == organizationId) {
            return;
        }

        const {setOrganization, setOrganizationRoleId, setOrganizationRoleToken} = props;
        const {organization_role_id, organization_role_token} = organization;

        // stop any in-flight/scheduled feed list requests for the old account
        if (fetchDataRequestSource.current) {
            fetchDataRequestSource.current.cancel('Request Canceled');
        }
        clearTimeout(fetchTimeout.current);
        lastRefreshTimestamp.current = null;

        // clear the selected feed BEFORE the account changes; otherwise the old feed gets
        // requested against the new account and 404s ("resource could not be found")
        navigate('MessageFlow', {organizationId: undefined, messageFeedId: undefined});
        setMessageFeeds([]);
        setNewFeeds([]);
        setPage(0);
        setIsLastPage(false);
        setTotalFilteredCount('--');
        // keep the filter update useEffect from running before filters are initialized for the next
        // organization; the organization effect above reloads them once the account changes
        setFiltersInitialized(false);

        await AsyncStorage.setItem('organizationId', organization.organization_id);
        await AsyncStorage.setItem('roleId', organization_role_id.toString());
        await AsyncStorage.setItem('roleToken', organization_role_token);

        setOrganization(organization);
        setOrganizationRoleId(organization_role_id);
        setOrganizationRoleToken(organization_role_token);
    };

    const headerHeight = useMemo(() => {
        let headerHeight = 0;
        if(screenWidth <= 630) {
            headerHeight += 80;
        }

        if (realToken && user && organization) {
            headerHeight += 60;
        }

        return headerHeight;
    });

    const fetchFilterData = useCallback(async () => {
        const userCountFilters = {
            message_feed_resolved: filters.message_feed_resolved,
            search: filters.search,
        };
        Promise.all([
            UsersService.getMessageflowUsers(organizationId, false),
            MessagesService.getMessageFeedUserCounts(organizationId, userCountFilters),
        ]).then(([usersData, countsData]) => {
            const countMap = {};
            (countsData.userCounts || []).forEach(c => {
                countMap[String(c.user_organization_id)] = c.count;
            });
            const merged = usersData.users.map(u => ({
                ...u,
                active_feeds: countMap[String(u.user_organization_id)] ?? 0,
            }));
            setMessageFeedUsers(merged);

            // Unresolved with no search: the per-user counts, unassigned included, add up to the
            // organization-wide unresolved total, so the status banner can skip its own request
            if (userCountFilters.message_feed_resolved === '0' && !userCountFilters.search) {
                const count = (countsData.userCounts || []).reduce((sum, c) => sum + Number(c.count), 0);
                setUserCountsUnresolved({organizationId, count});
            }
        });
    }, [organizationId, filters.message_feed_resolved, filters.search]);

    const fetchData = useCallback(async (isRefresh = false) => {
        let derp = fetchDataRequestSource.current?.cancel('Request Canceled');
        fetchDataRequestSource.current = CancelToken.source();

        if (fetchTimeout.current) {
            clearTimeout(fetchTimeout.current);
            fetchTimeout.current = null;
        }

        if (!isRefresh) {
            setIsLoading(true);
        }

        const updatedSince = isRefresh && lastRefreshTimestamp.current
            ? lastRefreshTimestamp.current
            : null;

        const fetchPage = isRefresh ? 0 : page;

        const refreshFilters = isRefresh
            ? {
                ...filters,
                selectedUserIds: [],
                includeUnassigned: false,
                message_feed_ids: newFeeds,
                message_feed_resolved: 'all',
            }
            : {...filters, message_feed_ids: newFeeds};

        const scheduleNextRefresh = () => {
            if (filters.search) {
                return;
            }

            clearTimeout(fetchTimeout.current);
            fetchTimeout.current = setTimeout(() => {
                fetchData(true);
                fetchFilterData();
            }, 5000);
        };

        let requestCanceled = false;

        MessagesService.getMessageFeeds(organizationId, {
            ...refreshFilters,
            includeTotalCount: !isRefresh,
            includeUnresolvedCount: false,
        }, fetchPage, perPage, fetchDataRequestSource.current?.token, updatedSince)
                .then(({messageFeeds, totalCount, serverTimestamp}) => {
                    if (!isRefresh) {
                        const isLastPageValue = messageFeeds.length < perPage;
                        setIsLastPage(isLastPageValue);
                    }

                    if (isRefresh) {
                        setMessageFeeds(current => {
                            const feedMatchesFilter = (feed) => {
                                // Status check
                                if (filters.message_feed_resolved !== null && filters.message_feed_resolved !== 'all') {
                                    if (filters.message_feed_resolved === '0') {
                                        if (feed.message_feed_resolved != 0 && feed.message_feed_resolved != null) {
                                            return false;
                                        }
                                    } else {
                                        if (feed.message_feed_resolved != 1) {
                                            return false;
                                        }
                                    }
                                }

                                // Assignment check
                                const assignmentFilterActive = filters.selectedUserIds.length > 0
                                    || filters.includeUnassigned;

                                if (assignmentFilterActive) {
                                    const assignedId = feed.message_feed_assigned_user_organization_id;
                                    const matchesSelectedUser = Boolean(assignedId)
                                        && filters.selectedUserIds.includes(String(assignedId));
                                    const matchesUnassigned = !assignedId && filters.includeUnassigned;

                                    if (!matchesSelectedUser && !matchesUnassigned) {
                                        return false;
                                    }
                                }

                                // Search check
                                if (filters.search) {
                                    const searchLower = filters.search.toLowerCase();
                                    const matchesSearch =
                                        (feed.message_feed_last_message && feed.message_feed_last_message.toLowerCase().includes(searchLower)) ||
                                        (feed.message_feed_phone_number_formatted && feed.message_feed_phone_number_formatted.includes(searchLower)) ||
                                        (feed.message_feed_phone_number && feed.message_feed_phone_number.includes(searchLower)) ||
                                        (feed.message_feed_contact_name && feed.message_feed_contact_name.toLowerCase().includes(searchLower));

                                    if (!matchesSearch) {
                                        return false;
                                    }
                                }

                                return true;
                            };

                            const existingFeedsMap = new Map();
                            current.forEach(feed => {
                                existingFeedsMap.set(feed.message_feed_id, feed);
                            });

                            const updatedFeedsMap = new Map();
                            const newFeedsToAdd = [];

                            messageFeeds.forEach(feed => {
                                const isExisting = existingFeedsMap.has(feed.message_feed_id);

                                if (isExisting) {
                                    updatedFeedsMap.set(feed.message_feed_id, feed);
                                } else {
                                    if (feedMatchesFilter(feed)) {
                                        newFeedsToAdd.push(feed);
                                    }
                                }
                            });

                            const mergedFeeds = current
                                .map(feed => {
                                    if (updatedFeedsMap.has(feed.message_feed_id)) {
                                        return updatedFeedsMap.get(feed.message_feed_id);
                                    }
                                    return feed;
                                })
                                .filter(feed => {
                                    return feedMatchesFilter(feed);
                                });

                            newFeedsToAdd.forEach(newFeed => {
                                mergedFeeds.push(newFeed);
                            });

                            let sortedFeeds = mergedFeeds.sort((messageFeedA, messageFeedB) => {
                                let includesA = newFeeds.includes(messageFeedA.message_feed_id);
                                let includesB = newFeeds.includes(messageFeedB.message_feed_id);
                                if (includesA && includesB) {
                                    return 0;
                                } else if (includesA) {
                                    return -1;
                                } else if (includesB) {
                                    return 1;
                                }

                                if(!messageFeedB.message_feed_last_message_created_timestamp) {
                                    return 1;
                                }
                                if(!messageFeedA.message_feed_last_message_created_timestamp) {
                                    return -1;
                                }
                                const timeA = new Date(messageFeedA.message_feed_last_message_created_timestamp).getTime();
                                const timeB = new Date(messageFeedB.message_feed_last_message_created_timestamp).getTime();
                                if (timeA !== timeB) {
                                    return timeB - timeA;
                                }
                                // Same-second timestamps: tie-break on id like the API does, or the order flips every refresh
                                return messageFeedB.message_feed_id - messageFeedA.message_feed_id;
                            });

                            return sortedFeeds;
                        });

                        if (serverTimestamp) {
                            lastRefreshTimestamp.current = serverTimestamp;
                        }
                    } else {
                        setMessageFeeds(current => {
                            if (page === 0) {
                                return messageFeeds;
                            } else {
                                const existingIds = new Set(current.map(f => f.message_feed_id));
                                const newFeedsOnly = messageFeeds.filter(f => !existingIds.has(f.message_feed_id));
                                return [...current, ...newFeedsOnly];
                            }
                        });

                        if (page === 0 && serverTimestamp) {
                            lastRefreshTimestamp.current = serverTimestamp;
                        }
                    }

                    if (!isRefresh && totalCount != null) {
                        setTotalFilteredCount(totalCount);
                    }
                })
                .catch((e) => {
                    // a canceled request was superseded by a newer fetch (or the view is going
                    // away) — the newer fetch owns the refresh timer and loading state
                    if (e.__CANCEL__) {
                        requestCanceled = true;
                        return;
                    }

                    console.log('[MessageFlow] Error fetching message feeds: ', e);
                })
                .finally(() => {
                    if (requestCanceled) {
                        return;
                    }

                    setIsLoading(false);
                    setInitialized(true);
                    scheduleNextRefresh();
                });
    }, [organizationId, filters, page, newFeeds, initialized, fetchFilterData]);

    const [fetchDataDebounce] = useDebouncedCallback(async () => {
        await fetchData();
    }, 500);

    const updateSelectedMessageInList = (messageFeed) => {
        if(messageFeed.message_feed_id == selectedMessageFeedId) {
            setMessageFeeds(current => {
                const selectedMessageFeedIndex = current.findIndex(messageFeed => {
                    return messageFeed.message_feed_id == selectedMessageFeedId;
                })

                if (selectedMessageFeedIndex >= 0) {
                    current.splice(selectedMessageFeedIndex, 1, {
                        ...current[selectedMessageFeedIndex],
                        ...messageFeed
                    });

                    return [...current];
                }

                return current;
            });
            fetchFilterData();
        }
    };

    useEffect(() => {
      clearTimeout(fetchTimeout.current);
      lastRefreshTimestamp.current = null;
      setIsLastPage(false);
      setPage(0);
      setTotalFilteredCount('--');
    }, [filters]);

    useEffect(() => {
        if (filters.search) {
            clearTimeout(fetchTimeout.current);
        } else if (initialized && filtersInitialized) {
            clearTimeout(fetchTimeout.current);

            fetchTimeout.current = setTimeout(() => {
                fetchData(true);
                fetchFilterData();
            }, 5000);
        }

        return () => {
            clearTimeout(fetchTimeout.current);
        };
    }, [filters.search, initialized, fetchData, fetchFilterData, filtersInitialized]);

    useEffect(() => {
        if (filtersInitialized) {
            fetchDataDebounce();
        }

        return () => {
            if(fetchDataRequestSource.current) {
                fetchDataRequestSource.current.cancel();
            }
            clearTimeout(fetchTimeout.current);
        };
    }, [organizationId, filters, page, newFeeds, filtersInitialized]);

    // a selection left over from another account is ignored (see selectedMessageFeedId above);
    // also clear it from the navigation state so it can't linger
    useEffect(() => {
        if (selectedFeedOrganizationId && selectedFeedOrganizationId != organizationId) {
            navigate('MessageFlow', {organizationId: undefined, messageFeedId: undefined});
        }
    }, [selectedFeedOrganizationId, organizationId]);

    useEffect(() => {
        if (messageFeeds.length && page === 0 && (!selectedMessageFeedId)) {
            if(screenWidth > 550) {
                navigate('MessageFlow', {messageFeedId: messageFeeds[0].message_feed_id, organizationId})
            }
        }
    }, [selectedMessageFeedId, messageFeeds, page, screenWidth, newFeeds, organizationId]);

    useEffect(() => {
        const getUserOrganizations = async () => {
            const organizations = await UserOrganizationsService.getUserOrganizations();
            setUserOrganizations(organizations.filter((organization) => {
                return organization.organization_message_flow_active == 1 && (
                    (organization.organization_textline_enabled &&
                        organization.organization_textline_token &&
                        organization.organization_textline_group_uuid)
                    ||
                    (organization.organization_twilio_enabled &&
                        organization.organization_twilio_token &&
                        organization.organization_twilio_sid &&
                        organization.organization_twilio_service_sid)
                );
            }));
        };

        getUserOrganizations();
    }, [user]);

    useEffect( () => {
        if (fetchDataRequestSource) {
            let derp = fetchDataRequestSource.current?.cancel('Request Canceled');
        }
        clearTimeout(fetchTimeout.current);
        lastRefreshTimestamp.current = null;
    }, [organization]);

    useEffect(() => {
        UsersService.getMessageflowUsers(organizationId)
            .then((data) => {
                setMessageFeedUsers(data.users);
            });
    }, [organizationId]);

    useEffect(() => {
        const messageTypes = ['phone'];
        if (organization.organization_survey_email) {
            messageTypes.push('email');
        }
        setMessageTypes(messageTypes);
    }, [organization]);


    const measureAndOpenUserPicker = useCallback(() => {
        if (userPickerOpen) {
            setUserPickerOpen(false);
            return;
        }
        const el = userPickerRef.current;
        const containerEl = containerRef.current;
        if (el && containerEl && el.measureInWindow) {
            el.measureInWindow((elX, elY, elWidth, elHeight) => {
                containerEl.measureInWindow((containerX, containerY) => {
                    setUserDropdownPos({
                        top: elY - containerY + elHeight,
                        left: elX - containerX,
                        width: elWidth,
                    });
                    setFilterMenuOpen(false);
                    setUserPickerOpen(true);
                });
            });
        } else {
            setFilterMenuOpen(false);
            setUserPickerOpen(true);
        }
    }, [userPickerOpen]);

    const measureAndOpenFilterMenu = useCallback(() => {
        if (filterMenuOpen) {
            setFilterMenuOpen(false);
            return;
        }
        const el = filterMenuRef.current;
        const containerEl = containerRef.current;
        if (el && containerEl && el.measureInWindow) {
            el.measureInWindow((elX, elY, elWidth, elHeight) => {
                containerEl.measureInWindow((containerX, containerY) => {
                    // right-align the 230px menu to the filter button's right edge
                    setFilterDropdownPos({
                        top: elY - containerY + elHeight,
                        left: elX - containerX + elWidth - 230,
                    });
                    setUserPickerOpen(false);
                    setFilterMenuOpen(true);
                });
            });
        } else {
            setUserPickerOpen(false);
            setFilterMenuOpen(true);
        }
    }, [filterMenuOpen]);

    const getFeedType = useCallback((feed) => {
        if (feed.message_feed_email_address) return 'email';
        return 'text';
    }, []);

    const allUsersSelected = useMemo(() =>
        messageFeedUsers.length > 0 && filters.selectedUserIds.length === messageFeedUsers.length,
        [filters.selectedUserIds, messageFeedUsers]
    );

    const unassignedSelected = useMemo(() =>
        filters.includeUnassigned === true,
        [filters.includeUnassigned]
    );

    const filteredUsers = useMemo(() => {
        if (!userSearch) return messageFeedUsers;
        const lower = userSearch.toLowerCase();
        return messageFeedUsers.filter(u => getUserDisplayName(u).toLowerCase().includes(lower));
    }, [messageFeedUsers, userSearch]);

    const toggleUser = useCallback((userId) => {
        setFilters(current => {
            const ids = current.selectedUserIds;
            if (ids.includes(userId)) {
                return {
                    ...current,
                    selectedUserIds: ids.filter(id => id !== userId),
                };
            } else {
                return {
                    ...current,
                    selectedUserIds: [...ids, userId],
                };
            }
        });
    }, []);

    const toggleAllUsers = useCallback(() => {
        setFilters(current => {
            if (current.selectedUserIds.length === messageFeedUsers.length) {
                return {...current, selectedUserIds: []};
            } else {
                return {
                    ...current,
                    selectedUserIds: messageFeedUsers.map(u => String(u.user_organization_id)),
                };
            }
        });
    }, [messageFeedUsers]);

    const toggleUnassigned = useCallback(() => {
        setFilters(current => {
            const includeUnassigned = !current.includeUnassigned;
            return {
                ...current,
                includeUnassigned,
            };
        });
    }, []);

    const toggleType = useCallback((type) => {
        setFilters(current => {
            const types = current.activeTypes;
            if (types.includes(type)) {
                return {...current, activeTypes: types.filter(t => t !== type)};
            } else {
                return {...current, activeTypes: [...types, type]};
            }
        });
    }, []);

    const clearFilters = useCallback(() => {
        setFilters(current => ({
            ...current,
            message_feed_resolved: '0',
            sortOrder: 'newest',
            activeTypes: ['text', 'email', 'task'],
        }));
    }, []);

    const nonDefaultFilterCount = useMemo(() => {
        let count = 0;
        if (filters.message_feed_resolved === 'all') count++;
        if (filters.sortOrder === 'oldest') count++;
        if (filters.activeTypes.length < 3) count++;
        return count;
    }, [filters.message_feed_resolved, filters.sortOrder, filters.activeTypes]);

    const statusBarParts = useMemo(() => {
        const parts = [];
        if (filters.message_feed_resolved === 'all') parts.push('All statuses');
        if (filters.activeTypes.length < 3) {
            const typeLabels = { text: 'Texts', email: 'Emails', task: 'Tasks' };
            parts.push(filters.activeTypes.map(t => typeLabels[t]).join(' + '));
        }
        if (filters.sortOrder === 'oldest') parts.push('Oldest first');
        return parts;
    }, [filters.message_feed_resolved, filters.activeTypes, filters.sortOrder]);

    const unassignedChip = unassignedSelected ? (
        <View key="unassigned" style={styles.chipAll}>
            <AppText
                style={[
                    styles.chipAllText,
                    isDarkMode ? {color: theme.textSecondary} : {},
                ]}
            >
                Unassigned
            </AppText>

            <TouchableOpacity
                onPress={toggleUnassigned}
                hitSlop={{top: 8, bottom: 8, left: 4, right: 8}}
            >
                <AppText style={styles.chipAllX}>×</AppText>
            </TouchableOpacity>
        </View>
    ) : null;

    const renderUserChips = useCallback(() => {
        if (allUsersSelected) {
            return (
                <>
                {unassignedChip}
                <View style={styles.chipAll}>
                    <AppText
                        style={[styles.chipAllText, isDarkMode ? {color: theme.textSecondary} : {}]}
                    >
                        All users
                    </AppText>
                    <TouchableOpacity
                        onPress={() => setFilters(cur => ({...cur, selectedUserIds: []}))}
                        hitSlop={{top: 8, bottom: 8, left: 4, right: 8}}
                    >
                        <AppText style={[styles.chipAllX, isDarkMode ? {color: theme.textSecondary} : {}]}>×</AppText>
                    </TouchableOpacity>
                </View>
                </>
            );
        }
        if (filters.selectedUserIds.length === 0) {
            return unassignedChip || <AppText style={[
                styles.chipPlaceholder,
                isDarkMode ? {color: theme.text} : {}
            ]}>
                All conversations
            </AppText>;
        }
        return (
            <>
            {unassignedChip}
            {filters.selectedUserIds.map(userId => {
            const u = messageFeedUsers.find(u => String(u.user_organization_id) === userId);
            if (!u) return null;
            const firstName = getUserDisplayName(u).split(' ')[0];
            return (
                <View key={userId} style={styles.chip}>
                    <AppText style={styles.chipText}>{firstName}</AppText>
                    <TouchableOpacity
                        onPress={() => toggleUser(userId)}
                        hitSlop={{top: 8, bottom: 8, left: 4, right: 8}}
                    >
                        <AppText style={styles.chipX}>×</AppText>
                    </TouchableOpacity>
                </View>
            );
            })}
            </>
        );
    }, [allUsersSelected, filters.selectedUserIds, isDarkMode, messageFeedUsers, theme.text, theme.textSecondary, toggleUnassigned, toggleUser, unassignedSelected]);

    const getFeedTitle = useCallback((messageFeed) => {
        if (messageFeed.message_feed_email_address && !messageFeed.message_feed_contact_name) {
            return messageFeed.message_feed_email_address;
        }

        return messageFeed.message_feed_contact_name
            ? messageFeed.message_feed_contact_name
            : formatPhoneNumber(messageFeed.message_feed_phone_number_formatted)
    }, []);

    const getFeedPreview = useCallback((messageFeed) => {
        const lastMessage = (messageFeed.message_feed_last_message || '').trim();
        if (lastMessage) {
            return lastMessage;
        }

        // Don't return empty subject — fall through to better defaults
        const emailSubject = (messageFeed.message_feed_email_subject || '').trim();
        if (messageFeed.message_feed_email_address && emailSubject) {
            return emailSubject;
        }

        // Recent message with empty plain body (MMS / HTML-only email)
        if (messageFeed.message_id_recent || messageFeed.message_feed_last_message_at) {
            return messageFeed.message_feed_email_address ? 'Email' : 'Attachment';
        }

        return '(no message history)';
    }, []);

    const filterMenuContent = (
        <>
            <AppText style={styles.menuSectionLabel}>Status</AppText>
            {[
                { label: 'Unresolved', value: '0' },
                { label: 'All', value: 'all' },
            ].map(item => {
                const active = filters.message_feed_resolved === item.value;
                return (
                    <TouchableOpacity
                        key={item.value}
                        style={[styles.menuRow, active && styles.menuRowActive]}
                        onPress={() => setFilters(cur => ({...cur, message_feed_resolved: item.value}))}
                    >
                        <AppText style={[
                            styles.menuRowText,
                            active && styles.menuRowTextActive,
                            isDarkMode ? {color: theme.text} : {},
                            active && {color: theme.textSecondary},
                        ]}>
                            {item.label}
                        </AppText>
                        {active && <AppText style={styles.menuCheck}>✓</AppText>}
                    </TouchableOpacity>
                );
            })}
            <View style={styles.ddDivider} />
            <AppText style={styles.menuSectionLabel}>Show types</AppText>
            {[
                { label: 'Texts', value: 'text' },
                { label: 'Emails', value: 'email' },
                { label: 'Tasks', value: 'task' },
            ].map(item => {
                const active = filters.activeTypes.includes(item.value);
                return (
                    <TouchableOpacity
                        key={item.value}
                        style={[styles.menuRow, active && styles.menuRowActive]}
                        onPress={() => toggleType(item.value)}
                    >
                        <View style={[styles.menuCheckbox, active && styles.menuCheckboxActive]}>
                            {active && <AppText style={styles.checkboxCheck}>✓</AppText>}
                        </View>
                        <AppText style={[
                            styles.menuRowText,
                            active && styles.menuRowTextActive,
                            isDarkMode ? {color: theme.text} : {}
                        ]}>
                            {item.label}
                        </AppText>
                    </TouchableOpacity>
                );
            })}
            <View style={styles.ddDivider} />
            <AppText style={styles.menuSectionLabel}>Sort by</AppText>
            {[
                { label: 'Newest first', value: 'newest' },
                { label: 'Oldest first', value: 'oldest' },
            ].map(item => {
                const active = filters.sortOrder === item.value;
                return (
                    <TouchableOpacity
                        key={item.value}
                        style={[styles.menuRow, active && styles.menuRowActive]}
                        onPress={() => setFilters(cur => ({...cur, sortOrder: item.value}))}
                    >
                        <AppText style={[
                            styles.menuRowText,
                            active && styles.menuRowTextActive,
                            isDarkMode ? {color: theme.text} : {},
                            active && {color: theme.textSecondary},
                        ]}>
                            {item.label}
                        </AppText>
                        {active && <AppText style={styles.menuCheck}>✓</AppText>}
                    </TouchableOpacity>
                );
            })}
        </>
    );

    const userPickerContent = (
        <>
            <View style={styles.ddSearchWrapper}>
                <TextInput
                    style={styles.ddSearchInput}
                    placeholder="Search team..."
                    value={userSearch}
                    onChangeText={setUserSearch}
                />
            </View>
            <TouchableOpacity
                style={[styles.ddRow, allUsersSelected && styles.ddRowSelected]}
                onPress={toggleAllUsers}
            >
                <View style={[styles.checkbox, allUsersSelected && styles.checkboxSelected]}>
                    {allUsersSelected && <AppText style={styles.checkboxCheck}>✓</AppText>}
                </View>
                <AppText style={[
                    styles.ddRowName,
                    allUsersSelected && styles.ddRowNameSelected,
                    isDarkMode ? {color: theme.text} : {}
                ]}>
                    All users
                </AppText>
            </TouchableOpacity>
            <TouchableOpacity
                style={[styles.ddRow, unassignedSelected && styles.ddRowSelected]}
                onPress={toggleUnassigned}
            >
                <View style={[styles.checkbox, unassignedSelected && styles.checkboxSelected]}>
                    {unassignedSelected && <AppText style={styles.checkboxCheck}>✓</AppText>}
                </View>
                <AppText style={[styles.ddRowName, unassignedSelected && styles.ddRowNameSelected, isDarkMode ? {color: theme.text} : {}]}>
                    Unassigned
                </AppText>
            </TouchableOpacity>
            <View style={styles.ddDivider} />
            {filteredUsers.map(u => {
                const userId = String(u.user_organization_id);
                const selected = filters.selectedUserIds.includes(userId);
                return (
                    <TouchableOpacity
                        key={userId}
                        style={[styles.ddRow, selected && styles.ddRowSelected]}
                        onPress={() => toggleUser(userId)}
                    >
                        <View style={[styles.checkbox, selected && styles.checkboxSelected]}>
                            {selected && <AppText style={styles.checkboxCheck}>✓</AppText>}
                        </View>
                        <AppText style={[
                            styles.ddRowName,
                            selected && styles.ddRowNameSelected,
                            isDarkMode ? {color: theme.text} : {},
                            selected && isDarkMode ? {color: theme.textSecondary} : {}
                        ]}>
                            {getUserDisplayName(u)}
                        </AppText>
                        <View style={styles.ddCount}>
                            <AppText style={styles.ddCountText}>{u.active_feeds}</AppText>
                        </View>
                    </TouchableOpacity>
                );
            })}
        </>
    );

    const getMessageFeedTypeIcon = useCallback((feed) => {
        const props = {width: 11, height: 11};
        if (feed.message_feed_is_task == 1) {
            return <ChecklistSvg {...props} />
        }
        if (feed.message_feed_email_address) {
            return <MailAltSvg {...props} />
        }

        return <MessageSvg {...props} />
    }, []);

    const getMessageFeedTypeText = useCallback((feed) => {
        if (feed.message_feed_is_task == 1) {
            return 'Task';
        }
        if (feed.message_feed_email_address) {
            return 'Email';
        }

        return 'Text';
    }, []);

    const getFeedAssignedUserName = useCallback((feed) => {
        const match = messageFeedUsers.find(user => user.user_organization_id === feed.message_feed_assigned_user_organization_id);
        return match ? getUserDisplayName(match) : '';
    }, [messageFeedUsers]);

    return (
        <View style={[styles.messageFlowPageContainer, {maxHeight: screenHeight - headerHeight}]}>
            <View ref={containerRef} style={[styles.messageFeedsContainer, getThemeTransitionStyle(), isDarkMode && {
                backgroundColor: theme.background,
                borderRightColor: theme.border,
            }]}>
                <UnresolvedStatusBanner
                    organizationId={organizationId}
                    sharedStatus={userCountsUnresolved}
                    useSharedStatus={filters.message_feed_resolved === '0' && !filters.search}
                />

                    {/* Mobile: compact account row */}
                    {screenWidth <= 550 && userOrganizations.length > 0 && (
                        <AppPickerInput
                            title={'Account'}
                            enabled={!blockFilterTaps}
                            onValueChange={(value) => {
                                return switchOrganization(userOrganizations.find((organization) => organization.organization_id == value));
                            }}
                            selectedValue={organization.organization_id}
                            items={userOrganizations ?? []}
                            labelExtractor={'organization_title'}
                            valueExtractor={'organization_id'}
                            wrapperStyle={[{height: 'auto', marginBottom: 8}, isDarkMode && {backgroundColor: theme.inputBg, borderColor: theme.inputBorder}]}
                            labelStyle={isDarkMode ? {color: theme.textSecondary} : {}}
                            inputTextStyle={isDarkMode ? {color: theme.text} : {}}
                            arrowColor={isDarkMode ? theme.text : '#0065DB'}
                            arrowBorderColor={isDarkMode ? theme.inputBorder : '#E2E9F4'}
                        />
                    )}

                    {/* Row 1: User picker + (desktop) Account picker */}
                    <View style={[basicStyles.flexRow, {alignItems: 'stretch', marginBottom: 8}]}>
                        {/* User picker wrapper — ref used for outside-click detection and position measurement */}
                        <View ref={userPickerRef} style={{flex: 1, marginRight: (screenWidth > 550 && userOrganizations.length > 0) ? 8 : 0}}>
                            <TouchableOpacity
                                style={[styles.userPickerTrigger, isDarkMode && {backgroundColor: theme.inputBg, borderColor: theme.inputBorder}]}
                                onPress={() => {
                                    if (screenWidth <= 550) {
                                        setUserSheetOpen(true);
                                    } else {
                                        measureAndOpenUserPicker();
                                    }
                                }}
                            >
                                <View style={styles.chipContainer}>
                                    {renderUserChips()}
                                </View>

                                <ChevronDownSvg color={isDarkMode ? theme.text : '#0065DB'} />
                            </TouchableOpacity>
                        </View>

                        {/* Desktop: Account picker */}
                        {screenWidth > 550 && userOrganizations.length > 0 && (
                            <AppPickerInput
                                title={'Account'}
                                onValueChange={(value) => {
                                    return switchOrganization(userOrganizations.find((organization) => organization.organization_id == value));
                                }}
                                selectedValue={organization.organization_id}
                                items={userOrganizations ?? []}
                                labelExtractor={'organization_title'}
                                valueExtractor={'organization_id'}
                                wrapperStyle={isDarkMode ? {height: 'auto', backgroundColor: theme.inputBg, borderColor: theme.inputBorder} : {height: 'auto'}}
                                labelStyle={isDarkMode ? {color: theme.textSecondary} : {}}
                                inputTextStyle={isDarkMode ? {color: theme.text} : {}}
                                arrowColor={isDarkMode ? theme.text : '#0065DB'}
                                arrowBorderColor={isDarkMode ? theme.inputBorder : '#E2E9F4'}
                            />
                        )}
                    </View>

                    {/* Row 2: Search + count pill + filter button */}
                    <View style={[basicStyles.flexRow, {alignItems: 'center'}]}>
                        <View style={[styles.searchFieldWrapper, {flex: 1, marginTop: 0, marginRight: 8}, getThemeTransitionStyle(), isDarkMode && {
                            backgroundColor: theme.inputBg,
                            borderColor: theme.inputBorder,
                        }]}>
                            <TextInput
                                style={[styles.searchField, isDarkMode && {color: theme.text}]}
                                placeholder="Search"
                                placeholderTextColor={isDarkMode ? theme.textSecondary : undefined}
                                onChangeText={value => {
                                    setFilters(current => ({...current, search: value}));
                                }}
                                value={filters.search || ''}
                            />
                            <TouchableOpacity 
                                hitSlop={{top: 12, right: 12, bottom: 12, left: 12}}
                                onPress={() => setFilters(current => ({...current, search: null}))}>
                                <CloseSvg styles={{marginRight:4}} stroke={isDarkMode ? '#FFFFFF' : '#000000'} />
                            </TouchableOpacity>

                        </View>

                        {totalFilteredCount != null && (
                            <AppText style={[styles.countPill, isDarkMode && {color: theme.textSecondary}]}>
                                {totalFilteredCount}
                            </AppText>
                        )}

                        {/* Filter & Sort button wrapper — ref used for outside-click detection and position measurement */}
                        <View ref={filterMenuRef} style={{marginLeft: 6}}>
                            <TouchableOpacity
                                style={[styles.filterIconBtn, nonDefaultFilterCount > 0 && styles.filterIconBtnActive, isDarkMode && {backgroundColor: theme.inputBg, borderColor: theme.inputBorder}]}
                                onPress={() => {
                                    if (screenWidth <= 550) {
                                        setFilterSheetOpen(true);
                                    } else {
                                        measureAndOpenFilterMenu();
                                    }
                                }}
                            >
                                <AdjustmentsHorizontalSvg
                                    width={16}
                                    height={16}
                                    color={
                                        isDarkMode ? (
                                            nonDefaultFilterCount > 0 ? '#0065DB' : '#E2E9F4'
                                        ) : (
                                            // nonDefaultFilterCount > 0 ? '#4A4A4A' : '#E2E9F4'
                                            nonDefaultFilterCount > 0 ? '#4A4A4A' : '#E2E9F4'
                                        )
                                    }
                                />
                                {nonDefaultFilterCount > 0 && (
                                    <View style={styles.filterBadge}>
                                        <AppText style={styles.filterBadgeText}>{nonDefaultFilterCount}</AppText>
                                    </View>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>

                {/* Desktop dropdowns — rendered outside filtersWrapper so they paint over all siblings */}
                {userPickerOpen && screenWidth > 550 && (
                    <>
                        <TouchableOpacity activeOpacity={1} onPress={() => setUserPickerOpen(false)} style={styles.desktopDropdownBackdrop} />
                        <View
                            ref={userDropdownRef}
                            style={[
                                styles.userDropdown,
                                {position: 'absolute', top: userDropdownPos.top, left: userDropdownPos.left, width: Math.max(userDropdownPos.width, 240)},
                                isDarkMode && {backgroundColor: theme.inputBgSolid, borderColor: theme.inputBorder},
                            ]}>
                            {userPickerContent}
                        </View>
                    </>
                )}
                {filterMenuOpen && screenWidth > 550 && (
                    <>
                        <TouchableOpacity activeOpacity={1} onPress={() => setFilterMenuOpen(false)} style={styles.desktopDropdownBackdrop} />
                        <View
                            ref={filterDropdownRef}
                            style={[styles.filterMenu,
                                {position: 'absolute', top: filterDropdownPos.top, left: filterDropdownPos.left},
                                isDarkMode && {backgroundColor: theme.inputBgSolid, borderColor: theme.inputBorder},
                            ]}>
                            {filterMenuContent}
                        </View>
                    </>
                )}

                {/* Status bar — shown when any non-default filter is active */}
                {statusBarParts.length > 0 && (
                    <View style={[styles.statusBar, getThemeTransitionStyle(), isDarkMode && {backgroundColor: theme.selectedFeedBg, borderBottomColor: theme.border}]}>
                        <AppText style={[styles.statusBarText, isDarkMode && {color: theme.text}]}>
                            {statusBarParts.join(' · ')}
                        </AppText>
                        <TouchableOpacity onPress={clearFilters}>
                            <AppText style={[styles.statusBarReset, isDarkMode && {color: theme.text}]}>
                                Reset to default
                            </AppText>
                        </TouchableOpacity>
                    </View>
                )}

                {
                    isLoading && messageFeeds.length === 0 ?
                        <ActivityIndicator style={styles.loadingIndicator} size="large" color="#467AFF"/>
                        : null
                }
                {
                    initialized && !isLoading && (totalFilteredCount === 0 || (totalFilteredCount === '--' && messageFeeds.length === 0)) ?
                        <View style={[styles.messageFeedWrapper, {border: 'none', justifyContent: 'center', marginTop: 10}]}>
                            <AppText style={[styles.messengerIdentifierString, isDarkMode && {color: theme.text}]}>
                                No Messages Found
                            </AppText>
                        </View>
                        : null
                }
                <FlatList
                    style={[basicStyles.flexScale]}
                    scrollEventThrottle={16}
                    data={useMemo(() => {
                        if (newFeeds.length === 0) return messageFeeds;
                        return [...messageFeeds].sort((a, b) => {
                            const aNew = newFeeds.includes(a.message_feed_id);
                            const bNew = newFeeds.includes(b.message_feed_id);
                            if (aNew === bNew) return 0;
                            return aNew ? -1 : 1;
                        });
                    }, [messageFeeds, newFeeds])}
                    keyExtractor={({message_feed_id}) => message_feed_id}
                    onEndReachedThreshold={0.1}
                    onEndReached={() => {
                        if (!isLastPage && !isLoading) {
                            setPage(current => current + 1);
                        }
                    }}
                    renderItem={({item: messageFeed}) => {
                        const grammarCheck = inboxAiGrammar.checksByFeedId[String(messageFeed.message_feed_id)];
                        let timeSinceString = ' ';
                        if(messageFeed.message_feed_last_message_created_timestamp) {
                            const minuetsSinceCreated = getMinuetsSinceDate(messageFeed.message_feed_last_message_created_timestamp);
                            if (minuetsSinceCreated > 1440) {
                                timeSinceString = formatDate(messageFeed.message_feed_last_message_created_timestamp);
                            } else if (minuetsSinceCreated > 60) {
                                timeSinceString = `${Math.round((minuetsSinceCreated / 60))}h`;
                            } else {
                                timeSinceString = `${minuetsSinceCreated}m`;
                            }
                        }

                        return (
                            <TouchableOpacity
                                onPress={() => {
                                    navigate('MessageFlow', {messageFeedId: messageFeed.message_feed_id, organizationId})
                                }}
                                style={[
                                    styles.messageFeedItemContainer,
                                    getThemeTransitionStyle(),
                                    isDarkMode && {borderBottomColor: theme.border},
                                    selectedMessageFeedId === messageFeed.message_feed_id ? [styles.messageFeedActive, isDarkMode && {backgroundColor: theme.selectedFeedBg}] : {}
                                ]}
                            >
                                <View style={[styles.messageFeedWrapper]}>
                                    <View style={[styles.messageDetailsLeft]}>
                                        <AppText style={[styles.messengerIdentifierString, isDarkMode && {color: theme.text}]}>
                                            {getFeedTitle(messageFeed)}
                                        </AppText>
                                        <AppText style={[styles.messageSnippet, isDarkMode && {color: theme.text}]}>
                                            {getFeedPreview(messageFeed)}
                                        </AppText>
                                    </View>
                                    <View style={[styles.messageDetailsRight]}>
                                        <AppText style={[styles.timeSinceString, isDarkMode && {color: theme.textSecondary}]}>
                                            {timeSinceString}
                                        </AppText>
                                        {
                                            messageFeed.message_feed_resolved == 1 ?
                                                <MessageFeedResolvedSvg/>
                                                :
                                                <MessageFeedUnresolvedSvg/>
                                        }
                                    </View>
                                </View>

                                <View style={[styles.messageFeedItemChipContainer]}>
                                    {!!getFeedAssignedUserName(messageFeed) && (
                                        <View style={[
                                            styles.messageFeedItemChip,
                                            isDarkMode ? styles.messageFeedItemChipDark : styles.messageFeedItemChipLight,
                                        ]}>
                                            <AppText>
                                                {getFeedAssignedUserName(messageFeed)}
                                            </AppText>
                                        </View>
                                    )}

                                    <View style={[
                                        styles.messageFeedItemChip,
                                        isDarkMode ? styles.messageFeedItemChipDark : styles.messageFeedItemChipLight,
                                    ]}>
                                        {getMessageFeedTypeIcon(messageFeed)}

                                        <AppText>
                                            {getMessageFeedTypeText(messageFeed)}
                                        </AppText>
                                    </View>

                                    {grammarCheck && (
                                        <View
                                            accessibilityLabel={grammarCheck.status === 'complete' ? 'Grammar correction ready' : grammarCheck.status === 'error' ? 'Grammar correction failed' : 'Grammar correction in progress'}
                                            style={styles.inboxAiRowStatus}
                                        >
                                            <AppText style={[
                                                styles.inboxAiRowStatusText,
                                                grammarCheck.status === 'complete' && styles.inboxAiRowStatusComplete,
                                                grammarCheck.status === 'error' && styles.inboxAiRowStatusError,
                                            ]}>AI</AppText>
                                            {grammarCheck.status === 'in_progress'
                                                ? <ActivityIndicator style={styles.inboxAiRowStatusIcon} size="small" color="#0065DB"/>
                                                : <AppText style={[
                                                    styles.inboxAiRowStatusIcon,
                                                    styles.inboxAiRowStatusResult,
                                                    grammarCheck.status === 'complete' ? styles.inboxAiRowStatusComplete : styles.inboxAiRowStatusError,
                                                ]}>{grammarCheck.status === 'complete' ? '✓' : '!'}</AppText>}
                                        </View>
                                    )}
                                </View>
                            </TouchableOpacity>
                        );
                    }}
                />
                <View style={[styles.createNewMessageFeedContainer, getThemeTransitionStyle(), isDarkMode && {
                    backgroundColor: theme.background,
                    borderTopWidth: 1,
                    borderTopColor: theme.border,
        
        
                }]}>
                    <AppButton
                        label="Text +"
                        action={() => {
                            setNewMessageModalActive({active: true, type: 'phone'});
                        }}
                        style={{width: '50%', height: 42}}
                    />
                    <AppButton
                        label="Email +"
                        action={() => {
                            setNewMessageModalActive(() => ({active: true, type: 'email'}));
                        }}
                        style={{width: '50%', height: 42}}
                    />
                </View>

                {/* Mobile: User picker bottom sheet */}
                {userSheetOpen && screenWidth <= 550 && (
                    <>
                        <TouchableOpacity
                            style={styles.sheetScrim}
                            onPress={() => { setUserSheetOpen(false); setUserSearch(''); }}
                            activeOpacity={1}
                        />
                        <View style={[styles.sheet]}>
                            <View style={styles.sheetHandle} />
                            <View style={styles.sheetHeader}>
                                <AppText style={styles.sheetTitle}>View team members</AppText>
                                <TouchableOpacity
                                    style={styles.sheetClose}
                                    onPress={() => { setUserSheetOpen(false); setUserSearch(''); }}
                                >
                                    <AppText style={styles.sheetCloseText}>×</AppText>
                                </TouchableOpacity>
                            </View>
                            <View style={styles.ddSearchWrapper}>
                                <TextInput
                                    style={styles.ddSearchInput}
                                    placeholder="Search team..."
                                    value={userSearch}
                                    onChangeText={setUserSearch}
                                />
                            </View>
                            <ScrollView style={{flex: 1}}>
                                <TouchableOpacity
                                    style={[styles.sheetRow, allUsersSelected && styles.sheetRowActive]}
                                    onPress={toggleAllUsers}
                                >
                                    <View style={[styles.checkboxLg, allUsersSelected && styles.checkboxLgActive]}>
                                        {allUsersSelected && <AppText style={styles.checkboxCheck}>✓</AppText>}
                                    </View>
                                    <AppText style={[styles.sheetRowName, allUsersSelected && styles.sheetRowNameActive]}>
                                        All users
                                    </AppText>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={[styles.sheetRow, unassignedSelected && styles.sheetRowActive]}
                                    onPress={toggleUnassigned}
                                >
                                    <View style={[styles.checkboxLg, unassignedSelected && styles.checkboxLgActive]}>
                                        {unassignedSelected && <AppText style={styles.checkboxCheck}>✓</AppText>}
                                    </View>
                                    <AppText style={[styles.sheetRowName, unassignedSelected && styles.sheetRowNameActive]}>
                                        Unassigned
                                    </AppText>
                                </TouchableOpacity>
                                {filteredUsers.map(u => {
                                    const userId = String(u.user_organization_id);
                                    const selected = filters.selectedUserIds.includes(userId);
                                    return (
                                        <TouchableOpacity
                                            key={userId}
                                            style={[styles.sheetRow, selected && styles.sheetRowActive]}
                                            onPress={() => toggleUser(userId)}
                                        >
                                            <View style={[styles.checkboxLg, selected && styles.checkboxLgActive]}>
                                                {selected && <AppText style={styles.checkboxCheck}>✓</AppText>}
                                            </View>
                                            <AppText style={[styles.sheetRowName, selected && styles.sheetRowNameActive]}>
                                                {getUserDisplayName(u)}
                                            </AppText>
                                            <View style={styles.sheetCount}>
                                                <AppText style={styles.sheetCountText}>{u.active_feeds}</AppText>
                                            </View>
                                        </TouchableOpacity>
                                    );
                                })}
                            </ScrollView>
                        </View>
                    </>
                )}

                {/* Mobile: Filter & sort bottom sheet */}
                {filterSheetOpen && screenWidth <= 550 && (
                    <>
                        <TouchableOpacity
                            style={styles.sheetScrim}
                            onPress={() => setFilterSheetOpen(false)}
                            activeOpacity={1}
                        />
                        <View style={styles.sheet}>
                            <View style={styles.sheetHandle} />
                            <View style={styles.sheetHeader}>
                                <AppText style={styles.sheetTitle}>Filter & sort</AppText>
                                <TouchableOpacity style={styles.sheetClose} onPress={() => setFilterSheetOpen(false)}>
                                    <AppText style={styles.sheetCloseText}>×</AppText>
                                </TouchableOpacity>
                            </View>
                            <ScrollView style={{flex: 1}}>
                                <AppText style={styles.sheetGroupLabel}>Status</AppText>
                                {[
                                    { label: 'Unresolved only', value: '0' },
                                    { label: 'All', value: 'all' },
                                ].map(item => {
                                    const active = filters.message_feed_resolved === item.value;
                                    return (
                                        <TouchableOpacity
                                            key={item.value}
                                            style={[styles.sheetRow, active && styles.sheetRowActive]}
                                            onPress={() => setFilters(cur => ({...cur, message_feed_resolved: item.value}))}
                                        >
                                            <View style={[styles.radioLg, active && styles.checkboxLgActive]}>
                                                {active && <View style={styles.radioDot} />}
                                            </View>
                                            <AppText style={[styles.sheetRowName, active && styles.sheetRowNameActive]}>
                                                {item.label}
                                            </AppText>
                                        </TouchableOpacity>
                                    );
                                })}
                                <AppText style={styles.sheetGroupLabel}>Show types</AppText>
                                {[
                                    { label: 'Texts', value: 'text' },
                                    { label: 'Emails', value: 'email' },
                                    { label: 'Tasks', value: 'task' },
                                ].map(item => {
                                    const active = filters.activeTypes.includes(item.value);
                                    return (
                                        <TouchableOpacity
                                            key={item.value}
                                            style={[styles.sheetRow, active && styles.sheetRowActive]}
                                            onPress={() => toggleType(item.value)}
                                        >
                                            <View style={[styles.checkboxLg, active && styles.checkboxLgActive]}>
                                                {active && <AppText style={styles.checkboxCheck}>✓</AppText>}
                                            </View>
                                            <AppText style={[styles.sheetRowName, active && styles.sheetRowNameActive]}>
                                                {item.label}
                                            </AppText>
                                        </TouchableOpacity>
                                    );
                                })}
                                <AppText style={styles.sheetGroupLabel}>Sort by</AppText>
                                {[
                                    { label: 'Newest first', value: 'newest' },
                                    { label: 'Oldest first', value: 'oldest' },
                                ].map(item => {
                                    const active = filters.sortOrder === item.value;
                                    return (
                                        <TouchableOpacity
                                            key={item.value}
                                            style={[styles.sheetRow, active && styles.sheetRowActive]}
                                            onPress={() => setFilters(cur => ({...cur, sortOrder: item.value}))}
                                        >
                                            <View style={[styles.radioLg, active && styles.checkboxLgActive]}>
                                                {active && <View style={styles.radioDot} />}
                                            </View>
                                            <AppText style={[styles.sheetRowName, active && styles.sheetRowNameActive]}>
                                                {item.label}
                                            </AppText>
                                        </TouchableOpacity>
                                    );
                                })}
                            </ScrollView>
                        </View>
                    </>
                )}
            </View>
            <View style={[styles.messageFeedContainer, getThemeTransitionStyle(), isDarkMode && {backgroundColor: theme.background}, selectedMessageFeedId ? styles.messageFeedContainerActive : {}]}>
                {
                    selectedMessageFeedId ?
                        <MessageFeed
                            key={`${organizationId}:${selectedMessageFeedId}`}
                            organizationId={organizationId}
                            messageFeedId={selectedMessageFeedId}
                            updateSelectedMessageInList={updateSelectedMessageInList}
                            onBack={closeFeed}
                            onBackPressIn={armCloseFeedGuard}
                            inboxAiAvailable={inboxAiGrammar.available}
                            grammarCheck={inboxAiGrammar.checksByFeedId[String(selectedMessageFeedId)]}
                            checkGrammar={inboxAiGrammar.check}
                            clearGrammarCheck={inboxAiGrammar.clear}
                        />
                        : null
                }
            </View>
            {
                newMessageModalActive.active ?
                    <CreateNewMessageModal
                        handleSubmit={async (messageType, destination, subject = null) => {
                            const newMessageFeed = await MessagesService.insertMessageFeed(organizationId, messageType, destination, subject);
                            setNewFeeds([...newFeeds, newMessageFeed.message_feed_id]);
                            navigate('MessageFlow', {messageFeedId: newMessageFeed.message_feed_id, organizationId});
                            setNewMessageModalActive({active: false, type: null});
                            setPage(0);
                        }}
                        handleClose={() => {
                            setNewMessageModalActive({active: false, type: null});
                        }}
                        type={newMessageModalActive.type}

                    />
                    : null
            }
        </View>
    );
}

const mapStateToProps = (state) => {
    const {auth} = state;
    return {auth};
};
const mapDispatchToProps = dispatch => (
    bindActionCreators({
        setOrganization,
        setOrganizationRoleId,
        setOrganizationRoleToken
    }, dispatch)
);

const ConnectedMessageFlowInner = connect(mapStateToProps, mapDispatchToProps)(MessageFlowInner);

function MessageFlow(props) {
    return (
        <DarkModeProvider>
            <ConnectedMessageFlowInner {...props} />
        </DarkModeProvider>
    );
}

export default MessageFlow;

const styles = EStyleSheet.create({
    messageFlowPageContainer: {
        flexDirection: 'row',
        flex: 1,
    },
    messageFeedsContainer: {
        width: '25%',
        borderRightWidth: 1,
        borderRightColor: 'rgb(226, 233, 244)',
        backgroundColor: '#f8fafd',
        zIndex: 1,
        position: 'relative',
    },
    messageFeedContainer: {
        width: '75%',
        flex: 1,
        zIndex: 1,
        backgroundColor: '#f8fafd',
    },
    filtersWrapper: {
        padding: 15,
        borderBottomWidth: 1,
        borderBottomColor: '#E8EDF6',
        zIndex: 20,
    },
    // User picker trigger
    userPickerTrigger: {
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E2E9F4',
        borderRadius: 8,
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 6,
        paddingBottom: 6,
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: 57,
    },
    chipContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        flex: 1,
        alignItems: 'center',
    },
    chip: {
        backgroundColor: '#DCE9F8',
        borderRadius: 10,
        paddingLeft: 8,
        paddingRight: 4,
        paddingTop: 1,
        paddingBottom: 1,
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: 4,
        marginBottom: 2,
    },
    chipText: {
        color: '#0C447C',
        fontSize: 11,
        fontWeight: '500',
    },
    chipX: {
        color: '#0C447C',
        fontSize: 13,
        paddingLeft: 2,
        paddingRight: 2,
    },
    chipAll: {
        backgroundColor: '#1E6FCC',
        borderRadius: 10,
        paddingLeft: 10,
        paddingRight: 4,
        paddingTop: 1,
        paddingBottom: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    chipAllText: {
        color: '#FFFFFF',
        fontSize: 11,
        fontWeight: '500',
    },
    chipAllX: {
        color: '#FFFFFF',
        fontSize: 13,
        paddingLeft: 6,
        paddingRight: 2,
    },
    chipPlaceholder: {

    },
    pickerChevron: {
        color: '#4A4A4A',
        fontSize: 14,
        marginLeft: 6,
    },
    // Desktop dropdown (user picker) — position/top/left set as inline props at render time
    userDropdown: {
        position: 'absolute',
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#D4D4D7',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        zIndex: 100,
        paddingTop: 4,
        paddingBottom: 4,
        marginTop: 4,
    },
    // Desktop filter & sort dropdown — position/top/right set as inline props at render time
    filterMenu: {
        position: 'absolute',
        width: 230,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#D4D4D7',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        zIndex: 100,
        paddingTop: 4,
        paddingBottom: 4,
        marginTop: 4,
    },
    // Dropdown shared row styles
    ddSearchWrapper: {
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 8,
        paddingBottom: 6,
    },
    ddSearchInput: {
        height: 32,
        fontSize: 12,
        paddingLeft: 10,
        paddingRight: 10,
        borderWidth: 1,
        borderColor: '#E5E5E7',
        borderRadius: 6,
        backgroundColor: '#FFFFFF',
        color: '#1A1A1A',
    },
    ddRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 8,
        paddingBottom: 8,
        cursor: 'pointer',
    },
    ddRowSelected: {
        backgroundColor: '#EAF2FB',
    },
    ddRowName: {
        flex: 1,
        fontSize: 13,
        color: '#1A1A1A',
        marginLeft: 8,
    },
    ddRowNameSelected: {
        color: '#0C447C',
        fontWeight: '600',
    },
    ddCount: {
        backgroundColor: '#F4F4F5',
        borderRadius: 8,
        paddingLeft: 7,
        paddingRight: 7,
        paddingTop: 1,
        paddingBottom: 1,
        minWidth: 18,
        alignItems: 'center',
    },
    ddCountText: {
        fontSize: 11,
        color: '#4A4A4A',
        fontWeight: '600',
    },
    ddDivider: {
        height: 1,
        backgroundColor: '#E5E5E7',
        marginTop: 4,
        marginBottom: 4,
    },
    checkbox: {
        width: 14,
        height: 14,
        borderRadius: 3,
        borderWidth: 1.5,
        borderColor: '#D4D4D7',
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    checkboxSelected: {
        backgroundColor: '#1E6FCC',
        borderColor: '#1E6FCC',
    },
    checkboxCheck: {
        color: '#FFFFFF',
        fontSize: 9,
        fontWeight: '700',
        lineHeight: 14,
    },
    // Filter & sort button
    filterIconBtn: {
        width: 36,
        height: 36,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#E2E9F4',
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
    },
    filterIconBtnActive: {
        borderColor: '#1E6FCC',
        backgroundColor: '#DCE9F8',
    },
    filterBadge: {
        position: 'absolute',
        top: -4,
        right: -4,
        backgroundColor: '#1E6FCC',
        borderRadius: 8,
        minWidth: 14,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 1,
        paddingBottom: 1,
        alignItems: 'center',
    },
    filterBadgeText: {
        color: '#FFFFFF',
        fontSize: 9,
        fontWeight: '700',
        lineHeight: 12,
    },
    // Result count pill
    countPill: {
        fontSize: 11,
        color: '#4A4A4A',
        paddingLeft: 4,
        paddingRight: 4,
        fontWeight: '500',
        flexShrink: 0,
        minWidth: 34,
        textAlign: 'center'
    },
    // Filter menu rows
    menuSectionLabel: {
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 10,
        paddingBottom: 4,
        fontSize: 10,
        color: '#8A8A8A',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.7,
    },
    menuRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 9,
        paddingBottom: 9,
        cursor: 'pointer',
    },
    menuRowActive: {},
    menuRowText: {
        flex: 1,
        fontSize: 13,
        color: '#1A1A1A',
        marginLeft: 8,
    },
    menuRowTextActive: {
        color: '#1E6FCC',
        fontWeight: '600',
    },
    menuCheck: {
        fontSize: 13,
        color: '#1E6FCC',
    },
    menuCheckbox: {
        width: 14,
        height: 14,
        borderRadius: 3,
        borderWidth: 1.5,
        borderColor: '#D4D4D7',
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    menuCheckboxActive: {
        backgroundColor: '#1E6FCC',
        borderColor: '#1E6FCC',
    },
    // Status bar
    statusBar: {
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 8,
        paddingBottom: 8,
        backgroundColor: '#EAF2FB',
        borderBottomWidth: 1,
        borderBottomColor: '#E5E5E7',
        flexDirection: 'row',
        alignItems: 'center',
    },
    statusBarText: {
        flex: 1,
        fontSize: 11,
        color: '#0C447C',
        marginRight: 8,
    },
    statusBarReset: {
        fontSize: 11,
        color: '#0C447C',
        fontWeight: '600',
        cursor: 'pointer',
    },
    // Mobile bottom sheets
    desktopDropdownBackdrop: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99,
    },
    sheetScrim: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        zIndex: 10,
    },
    sheet: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        zIndex: 11,
        maxHeight: '85%',
        flexDirection: 'column',
        boxShadow: '0 -8px 30px rgba(0,0,0,0.2)',
    },
    sheetHandle: {
        width: 36,
        height: 4,
        borderRadius: 2,
        backgroundColor: '#D4D4D7',
        marginTop: 8,
        marginBottom: 4,
        alignSelf: 'center',
    },
    sheetHeader: {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 8,
        paddingBottom: 12,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottomWidth: 1,
        borderBottomColor: '#E5E5E7',
    },
    sheetTitle: {
        fontSize: 15,
        fontWeight: '700',
        color: '#1A1A1A',
    },
    sheetClose: {
        backgroundColor: '#F4F4F5',
        width: 32,
        height: 32,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
    },
    sheetCloseText: {
        fontSize: 20,
        color: '#1A1A1A',
        lineHeight: 28,
    },
    sheetRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 14,
        paddingBottom: 14,
        minHeight: 52,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E5E7',
        cursor: 'pointer',
    },
    sheetRowActive: {
        backgroundColor: '#EAF2FB',
    },
    sheetRowName: {
        flex: 1,
        fontSize: 14,
        color: '#1A1A1A',
        fontWeight: '500',
        marginLeft: 12,
    },
    sheetRowNameActive: {
        color: '#0C447C',
        fontWeight: '700',
    },
    sheetCount: {
        backgroundColor: '#F4F4F5',
        borderRadius: 10,
        paddingLeft: 9,
        paddingRight: 9,
        paddingTop: 2,
        paddingBottom: 2,
        alignItems: 'center',
    },
    sheetCountText: {
        fontSize: 12,
        color: '#4A4A4A',
        fontWeight: '600',
    },
    sheetGroupLabel: {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 14,
        paddingBottom: 6,
        fontSize: 11,
        color: '#4A4A4A',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.7,
    },
    checkboxLg: {
        width: 22,
        height: 22,
        borderRadius: 6,
        borderWidth: 1.5,
        borderColor: '#D4D4D7',
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    checkboxLgActive: {
        backgroundColor: '#1E6FCC',
        borderColor: '#1E6FCC',
    },
    radioLg: {
        width: 22,
        height: 22,
        borderRadius: 11,
        borderWidth: 1.5,
        borderColor: '#D4D4D7',
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    radioDot: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: '#FFFFFF',
    },
    // Existing styles (unchanged)
    widthHalf: {
        width: '50%'
    },
    searchFieldWrapper: {
        backgroundColor: '#FFFFFF',
        flexDirection: 'row',
        alignItems: 'center',
        height: 40,
        paddingLeft: 8,
        paddingRight: 12,
        borderWidth: 1,
        borderColor: '#E2E9F4',
        borderRadius: 30,
    },
    searchField: {
        flex: 1,
        marginLeft: 10,
        marginRight: 10
    },
    messageFeedItemContainer: {
        borderBottomWidth: 1,
        borderBottomColor: '#E8EDF6',
        paddingTop: 12,
        paddingBottom: 12,
        paddingLeft: 16,
        paddingRight: 16,
    },
    messageFeedWrapper: {
        flexDirection: 'row',
    },
    messageFeedItemChipContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingTop: 6,
    },
    messageFeedItemChip: {
        flexDirection: 'row',
        gap: 4,
        fontSize: 11,
        paddingVertical: 2,
        paddingHorizontal: 7,
        borderRadius: 8,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    messageFeedItemChipDark: {
        backgroundColor: '#F4F4F5',
        color: '#4A4A4A',
        borderColor: '#E5E5E7',
    },
    messageFeedItemChipLight: {
        backgroundColor: '#F4F4F5',
        color: '#4A4A4A',
        borderColor: '#E5E5E7',
    },
    messageFeedActive: {
        backgroundColor: 'rgba(60, 166, 245, 0.1)'
    },
    createNewMessageFeedContainer: {
        padding: 15,
        flex: 1,
        flexDirection: 'row',
        gap: 10,
        alignItems: 'center',
        justifyContent: 'center',
        maxHeight: 80
    },
    messageDetailsLeft: {
        flex: 1
    },
    messageDetailsRight: {
        alignItems: 'flex-end'
    },
    inboxAiRowStatus: {
        height: 20,
        marginLeft: 'auto',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    inboxAiRowStatusIcon: {
        marginLeft: 4,
    },
    inboxAiRowStatusComplete: {
        color: '#12B76A',
    },
    inboxAiRowStatusError: {
        color: '#F04438',
    },
    inboxAiRowStatusResult: {
        fontSize: 17,
        lineHeight: 20,
        fontWeight: '700',
    },
    inboxAiRowStatusText: {
        color: '#0065DB',
        fontWeight: '700',
    },
    messageSnippet: {
        maxWidth: '100%',
        textWrap: 'nowrap',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        // color: 'rgba(158, 171, 204, 1)',
        color: '#333333',
        fontFamily: 'SourceSansPro-Light',
    },
    timeSinceString: {
        fontSize: 14,
        color: 'rgba(158, 171, 204, 1)',
        fontFamily: 'SourceSansPro-Light',
    },
    messengerIdentifierString: {
        fontFamily: 'SourceSansPro-Bold',
    },
    '@media (max-width: 1000)': {
        messageFeedsContainer: {
            width: '40%'
        },
        messageFeedContainer: {
            width: '60%'
        },
    },
    '@media (max-width: 630)': {
        messageFlowPageContainer: {
            maxHeight: 'auto'
        },
    },
    '@media (max-width: 550)': {
        messageFeedsContainer: {
            width: '100%'
        },
        messageFeedContainer: {
            width: '100%',
            position: 'absolute',
            left: '100%',
            top: 0,
            bottom: 0,
            zIndex: 1,
        },
        messageFeedContainerActive: {
            transform: [{translateX: '-100%'}]
        },
        filterIconBtn: {
            width: 44,
            height: 44,
        },
    },
    loadingIndicator: {
        position: 'absolute',
        top: '50%',
        left: 0,
        right: 0,
        marginRight: 'auto',
        marginLeft: 'auto'
    }
});
